import { notification } from 'antd';
import { EditorState } from 'draft-js';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { useGetVariantDetailQuery } from '../../../api/variantApi';
import { useUpdatePrimaryTranscriptMutation } from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import { VariantTranscript } from '../../redux';

interface UseVariantUpdatePrimaryTranscriptModalLogicProps {
  closeModal: () => void;
  variantLink: string;
  selectedPrimaryVariantTranscriptData: VariantTranscript | null;
  setNewPrimaryTranscript?: React.Dispatch<
    React.SetStateAction<VariantTranscript | null>
  >;
}

/**
 * Custom hook that encapsulates all the business logic for VariantUpdatePrimaryTranscriptModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useVariantUpdatePrimaryTranscriptModalLogic = ({
  closeModal,
  variantLink,
  selectedPrimaryVariantTranscriptData,
  setNewPrimaryTranscript,
}: UseVariantUpdatePrimaryTranscriptModalLogicProps) => {
  const {
    isLoading,
    setIsLoading,
    user,
    createModalOpts: createBaseModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: 'Primary transcript updated successfully',
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Mutation hook returns a [trigger, result] tuple
  const [updatePrimaryTranscriptMutation] =
    useUpdatePrimaryTranscriptMutation();

  const [commentEditorState, setCommentEditorState] = useState<EditorState>(
    () => htmlToEditorState(''),
  );

  const handleEditorChange = useCallback((newState: EditorState) => {
    setCommentEditorState(newState);
    editorStateToHtml(newState);
  }, []);

  const handleModalClose = () => {
    if (isLoading) {
      return;
    }
    closeModal();
    setNewPrimaryTranscript?.(null);
  };

  // Uses the global createModalOpts from useModalForm as base.
  // Only onCancel is overridden: besides closing, it also resets the
  // selected transcript state (setNewPrimaryTranscript?.(null)).
  const createModalOpts = (title: React.ReactNode) => ({
    ...createBaseModalOpts(title),
    onCancel: handleModalClose,
  });

  const selectedPrimaryVariantTranscriptDataKeyValues = useMemo(() => {
    if (selectedPrimaryVariantTranscriptData) {
      return [
        {
          label: 'Transcript',
          value: selectedPrimaryVariantTranscriptData?.transcript_external_id,
        },
        {
          label: 'cDNA',
          value: selectedPrimaryVariantTranscriptData?.cdna,
        },
        {
          label: 'Protein change',
          value: selectedPrimaryVariantTranscriptData?.protein_change,
        },
        {
          label: 'Consequence',
          value:
            selectedPrimaryVariantTranscriptData?.consequence_terms_single_string,
        },
        {
          label: 'Affected exon',
          value: selectedPrimaryVariantTranscriptData?.affected_exon,
        },
        {
          label: 'HGVS protein',
          value: selectedPrimaryVariantTranscriptData?.hgvs_protein,
        },
      ];
    }
  }, [variant, selectedPrimaryVariantTranscriptData]);

  const variantKeyValues = useMemo(() => {
    const variantData = variant;
    return [
      {
        label: 'Gene',
        value: `${variantData?.gene_symbol} ${variantData?.gene_name ? `(${variantData?.gene_name})` : ''}`,
      },
      {
        label: 'Transcript',
        value: `${variantData?.transcript_external_id} ${variantData?.mane ? `(${variantData?.mane})` : ''}`,
        transcript_external_id: variantData?.transcript_external_id,
      },
      {
        label: 'cDNA',
        value: variantData?.cdna,
      },
      {
        label: 'Variant type',
        value: variantData?.variant_type,
      },
      {
        label: 'Protein change',
        value: `${variantData?.protein_change || ''} / ${variantData?.protein_change_single_letter || ''}`,
      },
      {
        label: 'Consequence',
        value: variantData?.consequence,
      },
      {
        label: 'Start-end (length)',
        value: `${variantData?.genomic_location_start || ''} - ${variantData?.genomic_location_end || ''}${
          variantData?.length_calculated
            ? `(${variantData?.length_calculated})`
            : ''
        }`,
      },
      {
        label: 'Chromosome',
        value: variantData?.chromosome_symbol,
      },
      {
        label: 'Affected exon',
        value: variantData?.affected_exon,
      },
      {
        label: 'gHGVS GRCh37',
        value: variantData?.hgvs_genomic_hg19,
      },
      {
        label: 'gHGVS GRCh38',
        value: variantData?.hgvs_genomic,
      },
      {
        label: 'HGVS protein',
        value: variantData?.hgvs_protein,
      },
      {
        label: 'VCF string',
        value: variantData?.vcf_string,
      },
    ];
  }, [variant]);

  const variantTranscriptLists = useMemo(() => {
    if (variant && variant.id) {
      return variant?.variant_transcripts_list.map((item: any) => ({
        label: `${item?.transcript_external_id} ${item?.mane ? `(${item?.mane})` : ''}`,
        value: item.transcript_external_id,
      }));
    }
    return [];
  }, [variant]);

  const handleTranscriptChange = useCallback(
    (value: string) => {
      const selected = variant?.variant_transcripts_list.find(
        (t: any) => t.transcript_external_id === value,
      );
      if (selected && setNewPrimaryTranscript) {
        setNewPrimaryTranscript({
          transcript_id: selected.transcript_id ?? 0,
          transcript_external_id: selected.transcript_external_id ?? '',
          refseq_id: selected.refseq_id ?? '',
          enst_id: selected.enst_id ?? '',
          biotype: selected.biotype ?? '',
          mane_transcript_type: selected.mane ?? '',
          is_mane_select: selected.is_mane_select ?? false,
          is_mane_plus: selected.is_mane_plus ?? false,
          stable_id_version: selected.stable_id_version ?? 0,
          hgvsc: selected.hgvsc ?? '',
          hgvs_protein: selected.hgvs_protein ?? '',
          affected_exon: selected.affected_exon ?? '',
          consequence_terms: selected.consequence_terms ?? [],
          consequence_terms_single_string:
            selected.consequence_terms_single_string ?? '',
          cdna: selected.cdna ?? '',
          is_current_primary_transcript:
            selected.is_current_primary_transcript ?? false,
          protein_change: selected.protein_change ?? '',
        });
      }
    },
    [variant, setNewPrimaryTranscript],
  );

  const handleSaveChanges = async () => {
    try {
      setIsLoading(true);

      const comment = editorStateToHtml(commentEditorState);

      const requestData = {
        cdna: selectedPrimaryVariantTranscriptData?.cdna ?? variant?.cdna ?? '',
        transcript_primaryid:
          selectedPrimaryVariantTranscriptData?.transcript_external_id ??
          variant?.transcript_external_id ??
          '',
        variant_comment: comment,
      };

      // The mutation's invalidatesTags already covers VariantStages, so the
      // stages query refetches automatically — no manual dispatch needed
      await updatePrimaryTranscriptMutation({
        variant_id: variant?.id ?? 0,
        requestData,
      }).unwrap();

      setIsLoading(false);

      notification.success({
        message: 'Success!',
        description: 'Primary transcript updated successfully',
      });

      handleModalClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    variant,
    user,
    commentEditorState,
    variantLink,
    selectedPrimaryVariantTranscriptDataKeyValues,
    variantKeyValues,
    variantTranscriptLists,
    createModalOpts,
    handleEditorChange,
    handleTranscriptChange,
    handleSaveChanges,
  };
};

export default useVariantUpdatePrimaryTranscriptModalLogic;
